import React, { useState, useCallback } from "react";
import { useAtomValue } from "jotai";
import {
  Alert,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Link,
  IconButton,
  List,
  ListItem,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { validationErrorsAtom } from "@/atoms";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { t } from "i18next";

interface ErrorBoxProps {
  isShortened?: boolean;
}

const ErrorBox = ({ isShortened = false }: ErrorBoxProps) => {
  const errors = useAtomValue(validationErrorsAtom);
  const [open, setOpen] = useState(false);

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  if (!errors?.length) return null;

  const firstError = errors[0];
  const additionalCount = errors.length - 1;

  return (
    <>
      {isShortened ? (
        <Box
          data-testid="ssml-error-box"
          onClick={handleOpen}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            width: "100%",
            px: 2,
            py: 1.5,
            bgcolor: "rgba(211, 47, 47, 0.04)",
            borderRadius: "8px",
            cursor: "pointer",
            transition: "background 0.3s",
            "&:hover": {
              bgcolor: "rgba(211, 47, 47, 0.08)",
            },
            "&:hover .arrow": {
              transform: "translateX(3px)",
            },
          }}
        >
          <ErrorOutlineIcon
            sx={{ fontSize: "1.125rem", color: "error.main" }}
          />
          <Typography variant="body2" sx={{ color: "error.main", flex: 1 }}>
            SSML contains{" "}
            <Box component="span" sx={{ fontWeight: 700 }}>
              {errors.length}{" "}
              {errors.length === 1 ? "validation error" : "validation errors"}
            </Box>
          </Typography>
          <ChevronRightIcon
            className="arrow"
            sx={{
              fontSize: "1.125rem",
              color: "error.main",
              transition: "transform 0.2s ease",
              flexShrink: 0,
            }}
          />
        </Box>
      ) : (
        <Alert
          data-testid="ssml-error-box-alert"
          severity="error"
          icon={<ErrorOutlineIcon fontSize="inherit" />}
          sx={{
            alignItems: "center",
            "& .MuiAlert-message": { width: "100%" },
            bgcolor: "error.50",
            color: "error.900",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Typography variant="body2" component="span">
              {firstError.message}
            </Typography>
            {additionalCount > 0 && (
              <Link
                component="button"
                data-testid="ssml-error-box-button"
                variant="body2"
                onClick={handleOpen}
                underline="hover"
                sx={{
                  cursor: "pointer",
                  fontWeight: "bold",
                  color: "error.main",
                }}
              >
                + {additionalCount} {t("tts.errorBox.errors")}
              </Link>
            )}
          </Box>
        </Alert>
      )}

      {open && (
        <Dialog
          data-testid="ssml-error-box-dialog"
          open
          onClose={handleClose}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle
            sx={{
              m: 0,
              p: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              bgcolor: "error.50",
            }}
          >
            <Typography variant="h6" component="span">
              {t("tts.errorBox.validationErrors")} ({errors.length})
            </Typography>
            <IconButton
              data-testid="ssml-error-box-close"
              onClick={handleClose}
              size="small"
              sx={{ color: "text.secondary" }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers sx={{ p: 0 }}>
            <List sx={{ pt: 0 }}>
              {errors.map((error, index) => {
                const preview = error.preview || "";
                const id = error.errorIndexInPreview;
                const len = error.errorLength ?? 1;
                const hasHighlight =
                  typeof id === "number" &&
                  id >= 0 &&
                  len > 0 &&
                  id + len <= preview.length;

                return (
                  <React.Fragment key={index}>
                    <ListItem
                      alignItems="flex-start"
                      sx={{
                        py: 2,
                        flexDirection: "column",
                        alignItems: "stretch",
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.primary"
                        sx={{ mb: 1, fontWeight: 500 }}
                      >
                        {error.message}
                      </Typography>
                      {preview && (
                        <Box
                          component="pre"
                          sx={{
                            m: 0,
                            p: 1.5,
                            bgcolor: "grey.100",
                            borderRadius: 1,
                            fontSize: "0.80rem",
                            fontFamily:
                              "'IBM Plex Mono', 'Consolas', monospace",
                            color: "text.secondary",
                            overflowX: "auto",
                            border: "1px solid",
                            borderColor: "grey.300",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-all",
                          }}
                        >
                          {!hasHighlight ? (
                            preview
                          ) : (
                            <>
                              {preview.substring(0, id)}
                              <Box
                                component="span"
                                sx={{
                                  bgcolor: "#ffcdd2",
                                  color: "error.dark",
                                  borderRadius: "2px",
                                  fontWeight: "bold",
                                  borderBottom: "1px solid error.dark",
                                }}
                              >
                                {preview.substring(id, id + len)}
                              </Box>
                              {preview.substring(id + len)}
                            </>
                          )}
                        </Box>
                      )}
                    </ListItem>
                    {index < errors.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                );
              })}
            </List>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default ErrorBox;
